import { EntityDocument } from '@api/api/document-entity';
import { ManagementQuote } from '@api/api/earnings-insights';
import { Drawer } from '@design/components/drawer/Drawer';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useDocumentSections } from '@terminal/react-query-hooks/documents/useDocumentsEntity';
import { DocumentViewer } from '@terminal/views/alfred/document-search/document-viewer/DocumentViewer';
import { ManagementCitations } from '@terminal/widgets/citations-accordion-widget/components/management-citations/ManagementCitations';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '../../empty-state/EmptyState';
import styles from './ManagementQuotes.module.scss';

export interface ManagementQuotesProps {
  quotes: ManagementQuote[];
}

export const ManagementQuotes = ({ quotes }: ManagementQuotesProps) => {
  const { t } = useTranslation(['earnings', 'widget']);
  const [selectedDoc, setSelectedDoc] = useState<EntityDocument>();
  const [activeCitationIdx, setActiveCitationIdx] = useState<number>();
  const sectionIds = quotes.map(quote => quote.section_id);
  const { data, isFetching, isError } = useDocumentSections(sectionIds);

  if (isFetching || isError) return null;

  if (!data?.length) {
    return <EmptyState />;
  }

  const citations = data
    // PLATFORM-3029 - Temporary roundabout logic to filter out citations without a speaker name
    .filter(section => section.tags?.speakerName)
    .map(section => {
      const quote = quotes.find(
        quote => quote.section_id === section.id
      ) as ManagementQuote;
      return {
        ...section,
        ...quote,
      };
    });

  const onClickCitation = (citationId: string, document: EntityDocument) => {
    const citationIdx = citations.findIndex(c => c.id === citationId);

    if (citationIdx === -1) {
      setActiveCitationIdx(undefined);
      setSelectedDoc(undefined);
      return;
    }

    setActiveCitationIdx(citationIdx);
    setSelectedDoc(document);
  };

  const onClose = () => {
    setSelectedDoc(undefined);
    setActiveCitationIdx(undefined);
  };

  const textFormatter = (text?: string) => {
    if (!text) {
      return '';
    }

    return text
      .trim()
      .replace(/(\*\*)(.*?)\1/g, '$2')
      .replace(/\s+/g, ' ');
  };

  const getHighlightTexts = () => {
    const citation =
      activeCitationIdx !== undefined ? citations[activeCitationIdx] : null;

    if (!selectedDoc || selectedDoc.kind === 'presentation') {
      return [];
    }

    return [
      {
        text: textFormatter(citation?.text),
        fallbackText: textFormatter(citation?.content),
        pages: undefined,
        isPrimary: true,
      },
    ];
  };

  return (
    <div>
      <div className={styles.sectionTitle}>
        {t('earningsInsight.drawer.earningsRecap.items.managementQuotes')}
      </div>
      <div className={styles.quotesRoot}>
        <ManagementCitations citations={citations} onClick={onClickCitation} />
      </div>
      {!!selectedDoc && (
        <Drawer
          className={styles.styledDrawer}
          isOpen
          handleCloseClick={onClose}
        >
          <ErrorBoundaryWrapper>
            <div className={styles.drawerSectionWrapper}>
              <div className={styles.drawerSection}>
                <h2 className={styles.citationsHeaderTitle}>
                  {t(
                    'widget:assetOverview.knowledgeGraph.themeExposure.tabs.themeExposure.citations'
                  )}
                </h2>
                <ManagementCitations
                  citations={citations}
                  onClick={onClickCitation}
                  activeIdx={activeCitationIdx}
                />
              </div>
              <div className={styles.drawerSection}>
                {!!selectedDoc && (
                  <DocumentViewer
                    highlightTexts={getHighlightTexts()}
                    selectedCitation={{
                      document_id: selectedDoc.id,
                      document_name: selectedDoc.name,
                      document_type: selectedDoc.type,
                      pages: [],
                    }}
                  />
                )}
              </div>
            </div>
          </ErrorBoundaryWrapper>
        </Drawer>
      )}
    </div>
  );
};
